import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { demeterCalculatorsApi, demeterCompanyApi } from '../../../../Apis/Apis';
import {
    CalculationEngineMonthlyValueOverrides,
    DemeterCalculatorType,
    DemeterFeatureType,
    DemeterFilterTimeSpan,
    DemeterPermissionType,
    RunCalculationEngineResponse,
    RunTestCalculationEngineRequest,
    UnitOfMeasure,
} from '../../../../Generated/Raven-Demeter';
import { useApplicationDispatch, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { selectActingCompanyGuid, selectCompanyGuid, selectUserCurrentMarket, setActingDemeterCompanyGuid } from '../../../../Redux/Slices/UserSlice';
import useApi from '../../../Apis/Hooks/useApiHook';
import useApiWithoutAutoExecute from '../../../Apis/Hooks/useApiWithoutAutoExecute';
import useSymbolsApi from '../../../Apis/Hooks/useSymbolsApiHook';
import plusSvg from '../../../Assets/Icons/expandBlue.svg';
import chartService from '../../../Components/Charts/ChartService';
import ChartSkeleton from '../../../Components/Charts/ChartWrapper/ChartSkeleton';
import JoinedSelectionButtons from '../../../Components/Form/Buttons/JoinedSelectionButtons/JoinedSelectionButtons';
import LinkButton, { LinkButtonType } from '../../../Components/Form/Buttons/LinkButton';
import Dropdown from '../../../Components/Form/Inputs/Dropdown';
import LabelWithTooltip from '../../../Components/Form/Inputs/LabelWithTooltip';
import { SelectInputOption } from '../../../Components/Form/Inputs/SelectInput';
import ComponentHeader from '../../../Components/Headers/ComponentHeader';
import CardSkeleton from '../../../Components/Layout/CardSkeleton';
import TableSkeleton from '../../../Components/Layout/TableSkeleton';
import useFeatureFlag from '../../../Services/FeatureFlags/useFeatureFlagHook';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { EventDataTargetsEnum } from '../../../Services/Logging/DataLayerDefinitions';
import usePermission from '../../../Services/Permissions/usePermissionHook';
import { ValueOverrides } from '../../Calculators/ValorizationCalculator/ValorizationCalculatorDefinitions';
import {
    DemeterCalculationEngineLineShortModelWithChildren,
    HideGroup,
    MarginCompositeModel,
    MarginQuarterValue,
    MarginRequest,
    UpsertCalculationEngine,
} from '../RiskDefinitions';
import CustomCalculatorBar from './CustomCalculatorBar';
import CustomCalculatorModals from './CustomCalculatorModals';
import styles from './Margin.module.scss';
import MarginChartsCarousel from './MarginCharts/MarginChartsCarousel';
import MarginPositionsHedgeMapCharts from './MarginCharts/MarginPositionsHedgeMapCharts';
import EndingQuarterDropdown from './MarginDropdowns/EndingQuarterDropdown';
import StartingQuarterDropdown from './MarginDropdowns/StartingQuarterDropdown';
import MarginHeatMapTable from './MarginHeatMapTable/MarginHeatMapTable';
import MarginInputsTable from './MarginInputsTable/MarginInputsTable';
import MarginInputsTableFuturesBlendEditProducts, { AvailableFuturesProductOptions } from './MarginInputsTable/MarginInputsTableFuturesBlendActions';
import MarginPositionsTable from './MarginPositions/MarginPositionsTable';
import useCustomCalculators from './useCustomCalculators';

const numberOfMonthsInQuarter = 3;
const defaultNumberOfQuarters = 6;
const defalutDisplayDecimalPlaces = 2;

const excludedFuturesBlendPercentRows = ['cheeseFuturesPercent', 'dryWheyFuturesPercent', 'butterFuturesPercent', 'nonFatDryMilkFuturesPercent'];

const Margin: React.FC = () => {
    // Application hooks.
    const [translations] = useLanguage();
    const symbols = useSymbolsApi();
    const defaultMarket = useApplicationSelector(selectUserCurrentMarket);
    const defaultCompanyGuid = useApplicationSelector(selectCompanyGuid);
    const actingCompanyGuid = useApplicationSelector(selectActingCompanyGuid);
    const dispatch = useApplicationDispatch();
    const userIsFarmerMarginAdministrator = usePermission(DemeterPermissionType.FarmerMarginAdministrator);
    const isCustomCalculatorsEnabled = useFeatureFlag(DemeterFeatureType.FarmerMarginCustomCalculators);

    // Display hooks.
    const [displaySelection, setDisplaySelection] = useState('inputs');
    const [marginInputs, setMarginInputs] = useState<MarginRequest>();
    const [hideGroup, setHideGroup] = useState<HideGroup>({});
    const [selectedCalculator, setSelectedCalculator] = useState<SelectInputOption<string> & { groupName: string; demeterCalculationEngineGuid: string }>();
    const [numberOfDisplayProductionLots, setNumberOfDisplayProductionLots] = useState(0);
    const [availableFuturesProducts, setAvailableFuturesProducts] = useState<AvailableFuturesProductOptions[]>([]);

    // Request hook.
    const [runMonthlyValueOverrides, setRunMonthlyValueOverrides] = useState<Array<CalculationEngineMonthlyValueOverrides> | null>([]);
    const [upsertCalculationEngine, setUpsertCalculationEngine] = useState<UpsertCalculationEngine>({
        demeterCalculationEngineGuid: '',
        name: '',
        groupName: '',
        demeterCalculationEngineInputsGuid: '',
        valueOverrides: {} as { [key: string]: string },
        dataOverrides: {},
        monthlyValueOverrides: [],
        textOverrides: {},
    });

    // Api hooks.
    const [, refreshListFarmerMarginCalculator, listFarmerMarginCalculatorResponse] = useApi(() => demeterCalculatorsApi.listFarmerMarginCalculators());
    const [, , listCalculationEngineResponse] = useApi(() => demeterCalculatorsApi.listCalculationEngines(DemeterCalculatorType.FarmerMargin));

    const [, , listDemeterInsuranceCompaniesResponse] = useApi(() => {
        if (!userIsFarmerMarginAdministrator) {
            return null;
        }

        return demeterCompanyApi.listDemeterInsuranceCompanies();
    });

    const [runCalculationEngineLoading, refreshRunCalculationEngine, runCalculationEngineResponse] = useApiWithoutAutoExecute<
        RunTestCalculationEngineRequest,
        RunCalculationEngineResponse
    >((request) => demeterCalculatorsApi.runTestCalculationEngine(upsertCalculationEngine.demeterCalculationEngineGuid!, request));

    const handleResetOverrides = () => {
        setRunMonthlyValueOverrides([]);
        setUpsertCalculationEngine({ ...upsertCalculationEngine, valueOverrides: {} });
        setNumberOfDisplayProductionLots(0);
    };

    const {
        selectedSavedCalculatorGuid,
        setSelectedSavedCalculatorGuid,
        activeSavedCalculator,
        savedCalculatorOptions,
        handleSave,
        calculatorFlyoutOptions,
        isAddCalculatorNameModalOpen,
        pendingCalculatorName,
        setPendingCalculatorName,
        handleConfirmSaveAs,
        setIsAddCalculatorNameModalOpen,
        isRenameCalculatorModalOpen,
        pendingRenameCalculatorName,
        setPendingRenameCalculatorName,
        handleOpenRenameModal,
        handleConfirmRename,
        setIsRenameCalculatorModalOpen,
        isDeleteCalculatorModalOpen,
        handleOpenDeleteModal,
        handleConfirmDelete,
        setIsDeleteCalculatorModalOpen,
        isCreateCalculatorModalOpen,
        pendingCreateCalculatorName,
        setPendingCreateCalculatorName,
        handleConfirmCreate,
        setIsCreateCalculatorModalOpen,
    } = useCustomCalculators({
        upsertCalculationEngine,
        setUpsertCalculationEngine,
        runMonthlyValueOverrides,
        refreshListFarmerMarginCalculator,
        listFarmerMarginCalculatorResponse,
        selectedCalculator,
        handleResetOverrides,
        addedSuccessfullyMessage: translations.risk.margin.text.calculatorAddedSuccessfully,
        updatedSuccessfullyMessage: translations.risk.margin.text.calculatorUpdatedSuccessfully,
    });

    const [getFarmerMarginCalculatorByGuidLoading, refreshGetFarmerMarginCalculatorByGuid, getFarmerMarginCalculatorByGuidResponse] = useApiWithoutAutoExecute(
        () => demeterCalculatorsApi.getFarmerMarginCalculator(selectedCalculator!.demeterCalculationEngineGuid, selectedSavedCalculatorGuid),
    );
    const stateValueOverride = (upsertCalculationEngine.valueOverrides as ValueOverrides)[
        getFarmerMarginCalculatorByGuidResponse?.farmerMarginCalculator!.stateVariableName as keyof ValueOverrides
    ];
    const currentSelectionSavedRequestIsLoaded =
        getFarmerMarginCalculatorByGuidResponse?.farmerMarginCalculator?.demeterCalculationEngineGuid === upsertCalculationEngine.demeterCalculationEngineGuid;
    const getFarmerMarginCalculatorResponseStateOptions = getFarmerMarginCalculatorByGuidResponse?.farmerMarginCalculator?.stateOptions;

    // Tracks the previously-selected calculator so we only collapse expanded groups when the calculator
    // itself changes, not on every saved-data refresh that re-runs the hydration effect.
    const previousCalculatorGuidRef = useRef<string | undefined>(undefined);

    // Holds the pending debounced run of the calculation engine so keystrokes collapse into one POST.
    const runCalculationEngineDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const getQuarter = (date: Date) => {
        const month = date.getMonth() + 1;

        return { quarter: Math.ceil(month / numberOfMonthsInQuarter), year: date.getFullYear() };
    };

    const getQuarterRange = (currentQuarter: MarginQuarterValue) => {
        const currentQuarterIsOutOfBounds =
            (marginInputs?.startingQuarter?.quarter! > currentQuarter.quarter && marginInputs?.startingQuarter?.year! === currentQuarter.year) ||
            (marginInputs?.endingQuarter?.quarter! < currentQuarter.quarter && marginInputs?.endingQuarter?.year! === currentQuarter.year);
        const currentYearIsOutOfBounds = marginInputs?.startingQuarter?.year! > currentQuarter.year || marginInputs?.endingQuarter?.year! < currentQuarter.year;

        if (currentQuarterIsOutOfBounds || currentYearIsOutOfBounds) {
            return false;
        }

        return true;
    };

    const handleAddProductionLotLine = () => {
        if (runMonthlyValueOverrides && runMonthlyValueOverrides[0]) {
            const lengthOfTextOverrides = Object.keys(runMonthlyValueOverrides[0].textOverrides ?? {}).filter((x) => x.includes('lot')).length;

            if (lengthOfTextOverrides < numberOfDisplayProductionLots) {
                return;
            }
        }

        setNumberOfDisplayProductionLots(numberOfDisplayProductionLots + 1);
    };

    const handleRemoveProductionLotLines = () => {
        const newRunMonthlyValueOverrides = [...(runMonthlyValueOverrides ?? [])];
        const keysOfLotManagement = getFarmerMarginCalculatorByGuidResponse!.farmerMarginCalculator?.rows[0].productionLotLines.map((x) => x.variableName);

        newRunMonthlyValueOverrides!.forEach((x) => {
            x.textOverrides = {};
            keysOfLotManagement!.forEach((y, index) => {
                Object.keys(x.valueOverrides).forEach((z) => {
                    if (!z.includes(y)) {
                        return;
                    }

                    delete x.valueOverrides[z];
                });
                delete x.valueOverrides[`${y}${index + 1}`];
            });
        });

        setRunMonthlyValueOverrides(newRunMonthlyValueOverrides!);

        setNumberOfDisplayProductionLots(0);
    };

    const handleRemoveLotLineRow = (row: DemeterCalculationEngineLineShortModelWithChildren) => {
        const lotNumberToRemove = row.variableName.replace('lot', '');
        const newRunMonthlyValueOverrides = [...(runMonthlyValueOverrides ?? [])];
        const keysOfLotManagement = getFarmerMarginCalculatorByGuidResponse!.farmerMarginCalculator?.rows[0].productionLotLines.map((x) => x.variableName);
        const keysOfLotManagementValueOverrides = keysOfLotManagement?.slice(1);
        const keysOfTextOverrides = Object.keys(newRunMonthlyValueOverrides[0].textOverrides);
        const highestLotNumber = Math.max(...keysOfTextOverrides.map((y) => parseInt(y.replace('lot', ''), 10)), 0);

        setNumberOfDisplayProductionLots(numberOfDisplayProductionLots - 1);

        newRunMonthlyValueOverrides.forEach((x) => {
            const valueOverrides = Object.keys(x.valueOverrides);
            valueOverrides.forEach((y) => {
                keysOfLotManagementValueOverrides!.forEach((keyOfLotManagementValueOverrides) => {
                    const numberValueOfCurrentOverride = +y.replace(keyOfLotManagementValueOverrides, '');
                    if (numberValueOfCurrentOverride > +lotNumberToRemove) {
                        x.valueOverrides[`${keyOfLotManagementValueOverrides}${numberValueOfCurrentOverride - 1}`] = x.valueOverrides[y];
                    }
                });
            });

            keysOfTextOverrides.forEach((y) => {
                const numberValueOfCurrentTextOverride = +y.replace('lot', '');

                if (numberValueOfCurrentTextOverride > +lotNumberToRemove && x.textOverrides[y]) {
                    x.textOverrides[`lot${numberValueOfCurrentTextOverride - 1}`] = x.textOverrides[y];
                }
            });

            const currentLineNumber = +row.variableName.replace('lot', '');

            if (currentLineNumber > highestLotNumber) {
                return;
            }

            delete x.textOverrides[`lot${highestLotNumber}`];
            keysOfLotManagement!.forEach((y) => {
                delete x.valueOverrides[`${y}${highestLotNumber}`];
            });
        });

        setRunMonthlyValueOverrides(newRunMonthlyValueOverrides);
    };

    const getFarmerMarginCalculatorCompositeRowsModel = useMemo(() => {
        if (!getFarmerMarginCalculatorByGuidResponse) {
            return [];
        }

        return getFarmerMarginCalculatorByGuidResponse.farmerMarginCalculator?.rows
            .filter((x) => {
                const currentQuarter = getQuarter(new Date(x.asOfDate));
                return getQuarterRange(currentQuarter);
            })
            .map((x) => {
                const futuresBlendPriceAndPercentLevel2 = availableFuturesProducts
                    .map((y) => {
                        if (y.isChecked) {
                            return [y.lineValue1.price, y.lineValue1.percent, y.lineValue2.price, y.lineValue2.percent];
                        }

                        return [];
                    })
                    .flat();

                const outputHedgedPriceOrVolumeLevel3 = x.outputs.map((y) =>
                    y.lines
                        ?.map((z) => [
                            { ...z.volume, displayName: `${z.volume.displayName} (${translations.unitOfMeasure[`Short${z.volume.unitOfMeasure!}`]})` },
                            z.price,
                        ])
                        .flat(),
                );

                const outputHedgedPriceAndVolumeLevel2 = [
                    ...x.outputs
                        .map((y, index) => [
                            {
                                ...y.hedgedVolume,
                                displayName: `${y.hedgedVolume.displayName} (${translations.unitOfMeasure[`Short${y.hedgedVolume.unitOfMeasure!}`]})`,
                                children: outputHedgedPriceOrVolumeLevel3[index],
                                combinedWith: [y.hedgedPrice],
                            },
                            y.hedgedPrice,
                        ])
                        .flat(),
                ];

                const costPriceAndVolumeLevel3 = x.costs
                    .map((y) => [
                        {
                            ...y.needs,
                            displayName: `${y.needs.displayName} (${translations.unitOfMeasure[`Short${y.needs.unitOfMeasure!}`]})`,
                        },
                        {
                            ...y.price,
                            displayName: `${y.price.displayName} (${translations.unitOfMeasure[`Short${y.price.unitOfMeasure!}`]})`,
                            isReadOnly: true,
                        },
                        {
                            ...y.hedgedVolume,
                            displayName: `${y.hedgedVolume.displayName} (${translations.unitOfMeasure[`Short${y.hedgedVolume.unitOfMeasure!}`]})`,
                            combinedWith: [y.hedgedPrice],
                            children: [
                                ...y.lines.map((z) => [
                                    { ...z.volume, displayName: `${z.volume.displayName} (${translations.unitOfMeasure[`Short${z.volume.unitOfMeasure!}`]})` },
                                    z.price,
                                ]),
                            ].flat(),
                        },

                        {
                            ...y.hedgedPrice,
                        },
                    ])
                    .flat();

                if (x.outputAdjustment) {
                    outputHedgedPriceAndVolumeLevel2.push(x.outputAdjustment);
                }

                const productionLotLinesShortModels: DemeterCalculationEngineLineShortModelWithChildren[] = [];
                for (let index = 0; index < numberOfDisplayProductionLots; index += 1) {
                    const keysOfTextOverrides = Object.keys(runMonthlyValueOverrides![0]?.textOverrides ?? {});
                    const highestLotNumber = Math.max(...keysOfTextOverrides.map((y) => parseInt(y.replace('lot', ''), 10)), 0);
                    const currentVariable = keysOfTextOverrides[index];
                    const variableNumberKey = currentVariable?.replace('lot', '') ?? highestLotNumber + 1;
                    const newLotLines = x.productionLotLines.map((y) => ({ ...y, variableName: `${y.variableName}${variableNumberKey}` }));

                    productionLotLinesShortModels.push(...newLotLines);
                }

                const fields = {
                    asOfDate: x.asOfDate,
                    futuresBlendPrice: {
                        ...x.futuresPrices.futuresBlendPrice,
                        stickyColumnElement: (
                            <MarginInputsTableFuturesBlendEditProducts
                                currentValue={x}
                                hideGroup={hideGroup}
                                setHideGroup={setHideGroup}
                                availableFuturesProducts={availableFuturesProducts}
                                setAvailableFuturesProducts={setAvailableFuturesProducts}
                                upsertCalculationEngine={upsertCalculationEngine}
                                setUpsertCalculationEngine={setUpsertCalculationEngine}
                                runMonthlyValueOverrides={runMonthlyValueOverrides}
                                setRunMonthlyValueOverrides={setRunMonthlyValueOverrides}
                            />
                        ),
                        children: futuresBlendPriceAndPercentLevel2,
                    },
                    productionLines: [
                        ...x.productionLines.map((y, index) => {
                            if (y.variableName === 'totalProductionQuantity') {
                                return {
                                    ...y,
                                    combinedWith: x.productionLines,
                                    children: [
                                        ...productionLotLinesShortModels,
                                        {
                                            key: 'lotManagementRemoveRow',
                                            stickyColumnElement: (
                                                <LabelWithTooltip
                                                    title={
                                                        <button className={styles.margin_add_lot} onClick={handleAddProductionLotLine} type="button">
                                                            <img src={plusSvg} alt="#" />
                                                            {translations.risk.margin.text.addLot}
                                                        </button>
                                                    }
                                                    tooltip={translations.risk.margin.text.ensureLotName}
                                                    position="middle"
                                                />
                                            ),
                                            rowElement: (
                                                <div className={styles.margin_remove_all_lots_row}>
                                                    <LinkButton
                                                        onClick={handleRemoveProductionLotLines}
                                                        title={translations.risk.margin.text.removeAllLots}
                                                        type={LinkButtonType.White}
                                                        track={{ eventCategory: 'MarginCalculator', eventAction: 'RemoveAllLotsClicked' }}
                                                    />
                                                </div>
                                            ),
                                        },
                                    ],
                                };
                            }

                            return {
                                ...y,
                                isBorderless: index === 1,
                                displayName:
                                    y.unitOfMeasure === UnitOfMeasure.Head
                                        ? y.displayName
                                        : `${y.displayName} (${translations.unitOfMeasure[`Short${y.unitOfMeasure!}`]})`,
                            };
                        }),
                    ],
                    production: {
                        ...x.production,
                        displayName: `${x.production.displayName} (${translations.unitOfMeasure[`Short${x.production.unitOfMeasure!}`]})`,
                    },
                    outputHedgedVolume: {
                        ...x.outputHedgedVolume,
                        displayName: `${x.outputHedgedVolume.displayName} (${translations.unitOfMeasure[`Short${x.outputHedgedVolume.unitOfMeasure!}`]})`,
                        children: outputHedgedPriceAndVolumeLevel2,
                        combinedWith: [x.outputHedgedPrice],
                    },
                    outputHedgedPrice: {
                        ...x.outputHedgedPrice,
                    },
                    costTotalPrice: {
                        ...x.costTotalPrice,
                        displayName: `${x.costTotalPrice.displayName} (${chartService.getCurrencyAndUnitOfMeasureText(
                            x.costTotalPrice.unitOfMeasure,
                            x.costTotalPrice.currency,
                        )})`,
                        hideGroup: hideGroup[x.costTotalPrice.variableName],
                        children: [
                            {
                                ...x.costLinesPrice,
                                displayName: `${x.costLinesPrice.displayName} (${translations.unitOfMeasure[`Short${x.costLinesPrice.unitOfMeasure!}`]})`,
                                children: [...costPriceAndVolumeLevel3],
                            },
                            {
                                ...x.costOtherPrice,
                                displayName: `${x.costOtherPrice.displayName} (${translations.unitOfMeasure[`Short${x.costOtherPrice.unitOfMeasure!}`]})`,
                            },
                        ],
                    },
                    hedgedMargin: {
                        ...x.marginHedged,
                        displayName: `${x.marginHedged.displayName} (${chartService.getCurrencyAndUnitOfMeasureText(
                            x.marginHedged.unitOfMeasure,
                            x.marginHedged.currency,
                        )})`,
                    },
                    outputHedgedPercent: { ...x.outputHedgedPercent },
                    unhedgedMargin: {
                        ...x.marginUnhedged,
                        displayName: `${x.marginUnhedged.displayName}  (${chartService.getCurrencyAndUnitOfMeasureText(
                            x.marginUnhedged.unitOfMeasure,
                            x.marginUnhedged.currency,
                        )})`,
                    },
                    netMargin: {
                        ...x.marginNet,
                        displayName: `${x.marginNet.displayName}  (${chartService.getCurrencyAndUnitOfMeasureText(
                            x.marginNet.unitOfMeasure,
                            x.marginNet.currency,
                        )})`,
                    },
                };

                return fields;
            });
    }, [
        hideGroup,
        marginInputs,
        getFarmerMarginCalculatorByGuidResponse,
        runCalculationEngineResponse,
        numberOfDisplayProductionLots,
        availableFuturesProducts,
    ]);

    // Dropdown options.
    const availableQuarters = useMemo(() => {
        if (!runCalculationEngineResponse?.results) {
            return [];
        }
        const options = [];

        const thisQuarter = new Date(runCalculationEngineResponse.results[2].asOfDate!);
        const firstQuarter = getQuarter(thisQuarter);
        let currentYear = thisQuarter.getFullYear();

        let origionalQuarter = firstQuarter.quarter;
        const totalQuarters = defaultNumberOfQuarters + origionalQuarter - 1;

        for (let index = origionalQuarter; index <= totalQuarters; index += 1) {
            options.push({ label: `Q${origionalQuarter} ${currentYear}`, value: { quarter: origionalQuarter, year: currentYear } });

            if (origionalQuarter === 4) {
                origionalQuarter = 1;
                currentYear += 1;
            } else {
                origionalQuarter += 1;
            }
        }

        return options;
    }, [runCalculationEngineResponse?.results]);

    const stateOptions = useMemo(() => {
        if (!getFarmerMarginCalculatorResponseStateOptions || getFarmerMarginCalculatorResponseStateOptions.length === 0) {
            return [];
        }

        return [
            { label: translations.text.noState, value: undefined },
            ...(getFarmerMarginCalculatorByGuidResponse?.farmerMarginCalculator?.stateOptions ?? []),
        ];
    }, [getFarmerMarginCalculatorByGuidResponse]);

    const marginOptions = useMemo(() => {
        const options =
            listCalculationEngineResponse?.rows?.map((x) => ({
                label: x.displayName,
                value: x.demeterCalculationEngineGuid,
                demeterCalculationEngineGuid: x.demeterCalculationEngineGuid,
                groupName: x.displayName,
            })) ?? [];

        const milkIndex = options.findIndex((x) => x.label?.toLowerCase().includes('milk'));
        if (milkIndex > 0) {
            const [milkOption] = options.splice(milkIndex, 1);
            options.unshift(milkOption);
        }

        return options;
    }, [listCalculationEngineResponse]);

    const selectionGroupOptions = useMemo(
        () => [
            {
                label: translations.words.inputs,
                value: 'inputs',
            },
            {
                label: translations.positions.text.positions,
                value: 'positions',
            },
            {
                label: translations.words.charts,
                value: 'charts',
            },
        ],
        [],
    );

    const companiesList = useMemo(
        () =>
            listDemeterInsuranceCompaniesResponse?.rows?.map((x) => ({
                label: x.name!,
                value: x.demeterCompanyGuid!,
            })),
        [listDemeterInsuranceCompaniesResponse],
    );

    useEffect(() => {
        if (!getFarmerMarginCalculatorByGuidResponse || !selectedCalculator || !upsertCalculationEngine) {
            return;
        }

        const futuresLines = getFarmerMarginCalculatorByGuidResponse.farmerMarginCalculator!.rows[0].futuresPrices.lines;

        const availableProductsList = [];

        if (futuresLines.length > 0) {
            availableProductsList.push(
                {
                    displayName: translations.risk.margin.text.milkFutures,
                    isChecked: upsertCalculationEngine.textOverrides.futuresProducts?.includes('class3MilkFuturesPercent') ?? false,
                    lineValue1: futuresLines.find((y) => y.percent.variableName === 'class3MilkFuturesPercent')!,
                    lineValue2: futuresLines.find((y) => y.percent.variableName === 'class4MilkFuturesPercent')!,
                },
                {
                    displayName: translations.risk.margin.text.cheeseWhey,
                    isChecked: upsertCalculationEngine.textOverrides.futuresProducts?.includes('cheeseFuturesPercent') ?? false,
                    lineValue1: futuresLines.find((y) => y.percent.variableName === 'cheeseFuturesPercent')!,
                    lineValue2: futuresLines.find((y) => y.percent.variableName === 'dryWheyFuturesPercent')!,
                },
                {
                    displayName: translations.risk.margin.text.butterNonFatDryMilk,
                    isChecked: upsertCalculationEngine.textOverrides.futuresProducts?.includes('butterFuturesPercent') ?? false,
                    lineValue1: futuresLines.find((y) => y.percent.variableName === 'butterFuturesPercent')!,
                    lineValue2: futuresLines.find((y) => y.percent.variableName === 'nonFatDryMilkFuturesPercent')!,
                },
            );
        }

        setAvailableFuturesProducts(availableProductsList);
    }, [getFarmerMarginCalculatorByGuidResponse, selectedCalculator, upsertCalculationEngine.textOverrides.futuresProducts]);

    useEffect(() => {
        if (!defaultCompanyGuid || actingCompanyGuid || !companiesList || companiesList.length === 0) {
            return;
        }

        const initialDemeterCompany = companiesList.find((x) => x.value === defaultCompanyGuid)?.value ?? companiesList[0].value;
        dispatch(setActingDemeterCompanyGuid({ actingDemeterCompanyGuid: initialDemeterCompany }));
    }, [companiesList, defaultCompanyGuid]);

    useEffect(() => {
        if (userIsFarmerMarginAdministrator || !defaultCompanyGuid || actingCompanyGuid) {
            return;
        }

        dispatch(setActingDemeterCompanyGuid({ actingDemeterCompanyGuid: defaultCompanyGuid }));
    }, [defaultCompanyGuid]);

    useEffect(() => {
        if (!stateOptions || !getFarmerMarginCalculatorResponseStateOptions || getFarmerMarginCalculatorResponseStateOptions.length === 0) {
            return;
        }

        if (stateValueOverride !== undefined) {
            return;
        }

        setUpsertCalculationEngine({
            ...upsertCalculationEngine,
            valueOverrides: { [getFarmerMarginCalculatorByGuidResponse.farmerMarginCalculator!.stateVariableName]: stateOptions[1]?.value },
        });
    }, [stateOptions]);

    useEffect(() => {
        if (!actingCompanyGuid) {
            return;
        }

        refreshListFarmerMarginCalculator();
    }, [actingCompanyGuid]);

    // If we select a different calculator or saved inputs, check for saved version. Reset relevant items.
    useEffect(() => {
        if (!listFarmerMarginCalculatorResponse || !selectedCalculator) {
            return;
        }

        const changeKey = `${selectedCalculator.value}:${selectedSavedCalculatorGuid ?? ''}`;
        const calculatorChanged = previousCalculatorGuidRef.current !== changeKey;
        previousCalculatorGuidRef.current = changeKey;

        handleResetToLastSave({ preserveHideGroup: !calculatorChanged });
        refreshGetFarmerMarginCalculatorByGuid();

        if (!activeSavedCalculator && selectedCalculator) {
            setUpsertCalculationEngine({
                ...upsertCalculationEngine,
                groupName: selectedCalculator.groupName,
                demeterCalculationEngineGuid: selectedCalculator.demeterCalculationEngineGuid,
            });

            return;
        }

        const upsertRequest = {
            ...upsertCalculationEngine,
            groupName: selectedCalculator.groupName,
            demeterCalculationEngineGuid: activeSavedCalculator!.demeterCalculationEngineGuid,
            demeterCalculationEngineInputsGuid: activeSavedCalculator!.demeterCalculationEngineInputsGuid ?? '',
            valueOverrides: activeSavedCalculator!.valueOverrides ?? {},
            textOverrides: activeSavedCalculator!.textOverrides ?? {},
        };

        const stateVariableName = getFarmerMarginCalculatorByGuidResponse?.farmerMarginCalculator?.stateVariableName;
        const shouldPreserveUserStateOverride = !calculatorChanged && !!stateVariableName && stateValueOverride !== undefined;

        if (shouldPreserveUserStateOverride) {
            upsertRequest.valueOverrides = {
                ...upsertRequest.valueOverrides,
                [stateVariableName!]: stateValueOverride,
            };
        } else if (stateVariableName) {
            const stateDoesNotExistForUser = !stateOptions.find((x) => x.value === activeSavedCalculator?.valueOverrides[stateVariableName]);

            if (stateDoesNotExistForUser) {
                upsertRequest.valueOverrides = {};
            }
        }

        setUpsertCalculationEngine(upsertRequest);
    }, [
        listFarmerMarginCalculatorResponse,
        selectedCalculator,
        selectedSavedCalculatorGuid,
        getFarmerMarginCalculatorByGuidResponse?.farmerMarginCalculator?.demeterCalculationEngineGuid,
    ]);

    // When we have new criteria, re-run the calculation engine.
    useEffect(() => {
        const clearPendingRunCalculationEngineTimeout = () => {
            if (runCalculationEngineDebounceTimeoutRef.current) {
                clearTimeout(runCalculationEngineDebounceTimeoutRef.current);
                runCalculationEngineDebounceTimeoutRef.current = undefined;
            }
        };

        if (!listFarmerMarginCalculatorResponse?.rows || !upsertCalculationEngine.demeterCalculationEngineGuid || !getFarmerMarginCalculatorByGuidResponse) {
            return clearPendingRunCalculationEngineTimeout;
        }

        // If there is an unbalanced list, do not run the engine.
        const keysOfLotManagement = getFarmerMarginCalculatorByGuidResponse?.farmerMarginCalculator?.rows[0].productionLotLines
            .map((x) => x.variableName)
            .slice(1);

        const isClass3OrClass4Milk = upsertCalculationEngine.textOverrides.futuresProducts?.includes('class3MilkFuturesPercent') ?? false;
        const isCheeseOrDryWhey = upsertCalculationEngine.textOverrides.futuresProducts?.includes('cheeseFuturesPercent') ?? false;
        const isButterOrNonFatDryMilk = upsertCalculationEngine.textOverrides.futuresProducts?.includes('butterFuturesPercent') ?? false;

        const newRunMonthlyValueOverrides = [...(runMonthlyValueOverrides ?? [])];
        const lotManagementHasUnbalancedList = newRunMonthlyValueOverrides.some((x) => {
            const textOverrides = Object.keys(x.textOverrides ?? {}).map((y) => y.replace('lot', ''));
            const keysOfValueOverrides = Object.keys(x.valueOverrides ?? {}).filter((y) => keysOfLotManagement?.some((z) => y.includes(z)));
            const textOverridesAreMissing =
                (textOverrides.length === 0 || !keysOfValueOverrides.some((y) => textOverrides.find((z) => y.includes(z)))) &&
                keysOfValueOverrides.length !== 0;
            const numberOfActiveCells = textOverrides.length * keysOfLotManagement!.length;

            if (textOverridesAreMissing || textOverrides.length > numberOfActiveCells || keysOfValueOverrides.length % numberOfActiveCells) {
                return true;
            }

            return false;
        });

        if (lotManagementHasUnbalancedList && numberOfDisplayProductionLots > 1) {
            return clearPendingRunCalculationEngineTimeout;
        }

        // Debounce so keystrokes that update runMonthlyValueOverrides / valueOverrides collapse into
        // a single POST. Discrete dropdown actions (state / starting quarter) also fall under
        // this window but don't flip-flop within 250 ms, so the delay is imperceptible for them.
        clearPendingRunCalculationEngineTimeout();

        runCalculationEngineDebounceTimeoutRef.current = setTimeout(() => {
            refreshRunCalculationEngine({
                demeterCalculationEngineGuid: selectedCalculator?.demeterCalculationEngineGuid!,
                filterHistoryTimeSpan: DemeterFilterTimeSpan.OneMonth,
                showHistory: true,
                dataOverrides: {},
                valueOverrides: upsertCalculationEngine.valueOverrides,
                monthlyValueOverrides: runMonthlyValueOverrides?.map((x) => ({
                    ...x,
                    textOverrides: Object.keys(x.valueOverrides).some((y) => keysOfLotManagement?.find((z) => y.includes(z))) ? x.textOverrides : {},
                    valueOverrides: {
                        ...x.valueOverrides,
                        class3MilkFuturesPercent: isClass3OrClass4Milk ? x.valueOverrides?.class3MilkFuturesPercent ?? 100 : 0,
                        class4MilkFuturesPercent: isClass3OrClass4Milk ? x.valueOverrides?.class4MilkFuturesPercent ?? 0 : 0,
                        cheeseFuturesPercent: isCheeseOrDryWhey ? 100 : 0,
                        dryWheyFuturesPercent: 0,
                        butterFuturesPercent: isButterOrNonFatDryMilk ? 100 : 0,
                        nonFatDryMilkFuturesPercent: 0,
                    },
                })),
                textOverrides: upsertCalculationEngine.textOverrides,
            });
        }, 250);

        return clearPendingRunCalculationEngineTimeout;
    }, [
        listFarmerMarginCalculatorResponse,
        runMonthlyValueOverrides,
        upsertCalculationEngine.demeterCalculationEngineGuid,
        upsertCalculationEngine.valueOverrides,
        upsertCalculationEngine.textOverrides,
        selectedCalculator,
    ]);

    // Set the initial selected calculator once the options are available.
    // Wait for the saves list so we can prefer the engine matching the user's most recent save.
    // Set the initial selected calculator once the options are available.
    useEffect(() => {
        if (!marginOptions || marginOptions?.length === 0) {
            return;
        }

        // Only set initial calculator, refresh is handled by the selectedCalculator useEffect
        if (!selectedCalculator) {
            setSelectedCalculator(marginOptions[0]);
        }
    }, [marginOptions]);

    // Set initial margin inputs.
    useEffect(() => {
        if (!symbols || availableQuarters.length === 0) {
            return;
        }

        const currentQuarter = getQuarter(new Date());
        const currentQuarterIndex = availableQuarters.findIndex((x) => x.value.quarter === currentQuarter.quarter && x.value.year === currentQuarter.year) ?? 0;

        setMarginInputs({
            market: defaultMarket,
            product: symbols?.find((x) => x.symbolCategory === defaultMarket)!,
            startingQuarter:
                availableQuarters.find((x) => x.label === upsertCalculationEngine.textOverrides.startingQuarter)?.value ??
                availableQuarters[currentQuarterIndex]?.value,
            endingQuarter:
                availableQuarters.find((x) => x.label === upsertCalculationEngine.textOverrides.endingQuarter)?.value ??
                availableQuarters[currentQuarterIndex + 1]?.value,
        });
    }, [symbols, availableQuarters, upsertCalculationEngine.textOverrides.startingQuarter, upsertCalculationEngine.textOverrides.endingQuarter]);

    // When save is loaded, update the number of production plot lines.
    useEffect(() => {
        if (!getFarmerMarginCalculatorByGuidResponse || !runCalculationEngineResponse || numberOfDisplayProductionLots) {
            return;
        }

        const newNumberOfProductionLotLines = runCalculationEngineResponse
            ?.results![0].lines.filter((x) => x.variableName.includes('productionQuantity'))
            .filter((x) => x.value).length;

        setNumberOfDisplayProductionLots(newNumberOfProductionLotLines);
    }, [runCalculationEngineResponse, getFarmerMarginCalculatorByGuidResponse]);

    const handleResetToLastSave = (options?: { preserveHideGroup?: boolean }) => {
        const savedTextOverrides = { ...activeSavedCalculator?.monthlyValueOverrides[0]?.textOverrides };
        const origionalMonthlyOverrides = activeSavedCalculator?.monthlyValueOverrides.map((x) => ({
            ...x,
            asOfDate: formattingService.toApiDate(x.asOfDate),
            textOverrides: savedTextOverrides,
        }));

        setNumberOfDisplayProductionLots(Object.keys(savedTextOverrides ?? {}).length);
        if (!options?.preserveHideGroup) {
            setHideGroup({});
        }
        setRunMonthlyValueOverrides(origionalMonthlyOverrides ?? []);
        setUpsertCalculationEngine({
            ...upsertCalculationEngine,
            valueOverrides: activeSavedCalculator?.valueOverrides ?? {},
            textOverrides: activeSavedCalculator?.textOverrides ?? {},
        });
    };

    const handleSetStartingQuarter = (value: MarginQuarterValue) => {
        setUpsertCalculationEngine({
            ...upsertCalculationEngine,
            textOverrides: {
                ...upsertCalculationEngine.textOverrides,
                startingQuarter: availableQuarters.find((x) => x.value.quarter === value.quarter && x.value.year === value.year)?.label!,
            },
        });

        setMarginInputs({
            ...marginInputs!,
            startingQuarter: value,
        });
    };

    const handleSetEndingQuarter = (value: MarginQuarterValue) => {
        setUpsertCalculationEngine({
            ...upsertCalculationEngine,
            textOverrides: {
                ...upsertCalculationEngine.textOverrides,
                endingQuarter: availableQuarters.find((x) => x.value.quarter === value.quarter && x.value.year === value.year)?.label!,
            },
        });

        setMarginInputs({
            ...marginInputs!,
            endingQuarter: value,
        });
    };

    const hasNoEngineTypes = listCalculationEngineResponse !== undefined && marginOptions.length === 0;
    const isInitialLoading = !marginInputs && !hasNoEngineTypes;
    const loading =
        !hasNoEngineTypes &&
        (isInitialLoading ||
            !getFarmerMarginCalculatorCompositeRowsModel ||
            !getFarmerMarginCalculatorByGuidResponse ||
            getFarmerMarginCalculatorCompositeRowsModel?.length === 0 ||
            !runCalculationEngineResponse);

    const dropdownContainerReference = useRef<HTMLDivElement>(null);
    const [dropdownContainerWidth, setDropdownContainerWidth] = useState<number | undefined>();

    useLayoutEffect(() => {
        if (loading) {
            return undefined;
        }
        const containerElement = dropdownContainerReference.current;
        if (!containerElement) {
            return undefined;
        }
        const measureDropdownContainerWidth = () => {
            const { width } = containerElement.getBoundingClientRect();
            if (width > 0) {
                setDropdownContainerWidth((previousWidth) => Math.max(previousWidth ?? 0, width));
            }
        };
        const resizeObserver = new ResizeObserver(measureDropdownContainerWidth);
        resizeObserver.observe(containerElement);
        measureDropdownContainerWidth();
        return () => resizeObserver.disconnect();
    }, [loading]);

    const inputsTableContainerRef = useRef<HTMLDivElement>(null);
    const [inputsTableWidth, setInputsTableWidth] = useState<number | undefined>();

    useLayoutEffect(() => {
        if (displaySelection !== 'inputs' || loading) {
            setInputsTableWidth(undefined);
            return undefined;
        }
        const containerElement = inputsTableContainerRef.current;
        if (!containerElement) {
            return undefined;
        }

        const tableFlexElement = containerElement.firstElementChild as HTMLElement | null;
        const dataColumnElement = tableFlexElement?.lastElementChild as HTMLElement | null;
        if (!dataColumnElement) {
            return undefined;
        }

        const measureTableWidth = () => {
            const containerLeft = containerElement.getBoundingClientRect().left;
            const dataColumnRight = dataColumnElement.getBoundingClientRect().right;
            const measuredWidth = dataColumnRight - containerLeft;
            setInputsTableWidth(measuredWidth > 0 ? measuredWidth : undefined);
        };

        const resizeObserver = new ResizeObserver(measureTableWidth);
        resizeObserver.observe(dataColumnElement);
        measureTableWidth();
        return () => resizeObserver.disconnect();
    }, [displaySelection, loading]);

    return (
        <>
            {isInitialLoading ? (
                <CardSkeleton />
            ) : (
                <div className={styles.margin}>
                    <div>
                        <ComponentHeader title={translations.risk.margin.title} />
                        {!isInitialLoading && (
                            <div ref={dropdownContainerReference} data-testid="MarginCalculator" className={styles.margin_dropdown_inputs_container}>
                                <div className={styles.margin_dropdown}>
                                    <Dropdown
                                        label={translations.risk.margin.fields.calculator}
                                        value={marginOptions.find((x) => x.value === selectedCalculator?.value)?.value}
                                        options={marginOptions}
                                        handleOptionChange={(value) => {
                                            setSelectedCalculator(marginOptions.find((x) => x.value === value));
                                            // Switching engine types clears any named save selection so the page
                                            // reverts to "load last save for this engine" behavior.
                                            setSelectedSavedCalculatorGuid(undefined);
                                        }}
                                        disabled={marginOptions.length === 0 || displaySelection === 'charts'}
                                        track={{ eventCategory: 'MarginCalculator', eventAction: 'CalculatorChanged' }}
                                    />
                                </div>
                                {stateOptions.length > 0 && (
                                    <div className={styles.margin_dropdown}>
                                        <Dropdown
                                            label={translations.risk.margin.fields.state}
                                            value={stateValueOverride}
                                            options={stateOptions}
                                            handleOptionChange={(value) => {
                                                setUpsertCalculationEngine({
                                                    ...upsertCalculationEngine,
                                                    valueOverrides: {
                                                        [getFarmerMarginCalculatorByGuidResponse!.farmerMarginCalculator!.stateVariableName]: value,
                                                    },
                                                });
                                            }}
                                            disabled={
                                                getFarmerMarginCalculatorByGuidLoading ||
                                                !currentSelectionSavedRequestIsLoaded ||
                                                displaySelection === 'charts' ||
                                                displaySelection === 'positions'
                                            }
                                            track={{ eventCategory: 'MarginCalculator', eventAction: 'StateChanged' }}
                                        />
                                    </div>
                                )}
                                {marginInputs && (
                                    <div className={styles.margin_dropdown}>
                                        <StartingQuarterDropdown
                                            marginRequest={marginInputs}
                                            handleSetStartingQuarter={handleSetStartingQuarter}
                                            quarterOptions={availableQuarters.filter(
                                                (x) =>
                                                    (x.value.quarter <= marginInputs.endingQuarter.quarter &&
                                                        x.value.year === marginInputs.endingQuarter.year) ||
                                                    x.value.year < marginInputs.endingQuarter.year,
                                            )}
                                            disabled={loading || displaySelection === 'positions'}
                                        />
                                    </div>
                                )}

                                {marginInputs && (
                                    <div className={styles.margin_dropdown}>
                                        <EndingQuarterDropdown
                                            marginRequest={marginInputs}
                                            handleSetEndingQuarter={handleSetEndingQuarter}
                                            quarterOptions={availableQuarters.filter((x) => {
                                                const startingQuarterTimeValue = new Date(
                                                    marginInputs.startingQuarter?.year,
                                                    marginInputs.startingQuarter?.quarter,
                                                ).getTime();
                                                const currentQuarterTimeValue = new Date(x.value.year, x.value.quarter).getTime();

                                                if (currentQuarterTimeValue < startingQuarterTimeValue) {
                                                    return false;
                                                }

                                                return true;
                                            })}
                                            disabled={loading || displaySelection === 'positions'}
                                        />
                                    </div>
                                )}
                                {companiesList && companiesList.length > 0 && (
                                    <div className={styles.margin_dropdown}>
                                        <Dropdown
                                            label={translations.risk.margin.fields.company}
                                            value={companiesList?.find((x) => x.value === actingCompanyGuid)?.value}
                                            options={companiesList ?? []}
                                            handleOptionChange={(value) => {
                                                setSelectedSavedCalculatorGuid(undefined);
                                                dispatch(setActingDemeterCompanyGuid({ actingDemeterCompanyGuid: value }));
                                            }}
                                            disabled={
                                                !companiesList ||
                                                companiesList.length === 0 ||
                                                displaySelection === 'charts' ||
                                                displaySelection === 'positions'
                                            }
                                            track={{ eventCategory: 'MarginCalculator', eventAction: 'CompanyChanged' }}
                                        />
                                    </div>
                                )}
                                <CustomCalculatorBar
                                    savedCalculatorOptions={savedCalculatorOptions}
                                    selectedSavedCalculatorGuid={selectedSavedCalculatorGuid}
                                    onSelectSavedCalculator={(value) => {
                                        if (!value) {
                                            setSelectedSavedCalculatorGuid(undefined);
                                            return;
                                        }

                                        const selectedRow = listFarmerMarginCalculatorResponse?.rows?.find(
                                            (x) => x.demeterCalculationEngineInputsGuid === value,
                                        );

                                        if (selectedRow?.demeterCalculationEngineGuid) {
                                            const matchingEngine = marginOptions.find(
                                                (x) => x.demeterCalculationEngineGuid === selectedRow.demeterCalculationEngineGuid,
                                            );
                                            if (matchingEngine) {
                                                setSelectedCalculator(matchingEngine);
                                            }
                                        }

                                        setSelectedSavedCalculatorGuid(value);
                                    }}
                                    calculatorFlyoutOptions={calculatorFlyoutOptions}
                                    isVisible={isCustomCalculatorsEnabled && userIsFarmerMarginAdministrator}
                                    disabled={displaySelection === 'charts' || displaySelection === 'positions'}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div
                className={styles.margin_actions_buttons_row}
                style={{
                    ...(dropdownContainerWidth !== undefined ? { minWidth: dropdownContainerWidth } : {}),
                    ...(inputsTableWidth !== undefined ? { maxWidth: inputsTableWidth } : {}),
                }}
            >
                <JoinedSelectionButtons
                    name={EventDataTargetsEnum.MarginJoinedSelectionButton}
                    options={selectionGroupOptions}
                    handleSelectionChange={(selectionValue) => setDisplaySelection(selectionValue)}
                    selection={displaySelection}
                    testId="MarginSelectionButtons"
                    track={{ eventCategory: 'MarginCalculator', eventAction: 'DisplaySelectionChanged', eventValue: displaySelection }}
                />
                <div className={styles.margin_actions_save_display_area}>
                    <p>
                        {activeSavedCalculator?.modifiedAt &&
                            `${translations.words.saved} ${formattingService.toLongDateAndTimeFormat(new Date(activeSavedCalculator?.modifiedAt))}
                        ${translations.words.by} ${activeSavedCalculator?.modifiedBy}`}
                    </p>
                    <div className={styles.margin_actions_buttons}>
                        <LinkButton
                            onClick={handleResetToLastSave}
                            title={translations.actions.loadLastSave}
                            type={LinkButtonType.White}
                            disabled={loading}
                            track={{ eventCategory: 'MarginCalculator', eventAction: 'LoadLastSaveClicked' }}
                        />
                        <LinkButton
                            onClick={handleResetOverrides}
                            title={translations.actions.reset}
                            type={LinkButtonType.White}
                            disabled={loading}
                            track={{ eventCategory: 'MarginCalculator', eventAction: 'ResetClicked' }}
                        />
                        {isCustomCalculatorsEnabled && selectedSavedCalculatorGuid && userIsFarmerMarginAdministrator && (
                            <LinkButton
                                onClick={handleOpenRenameModal}
                                title={translations.risk.margin.headers.customCalculatorRename}
                                type={LinkButtonType.White}
                                disabled={loading}
                                track={{ eventCategory: 'MarginCalculator', eventAction: 'RenameClicked' }}
                            />
                        )}
                        {isCustomCalculatorsEnabled && selectedSavedCalculatorGuid && userIsFarmerMarginAdministrator && (
                            <LinkButton
                                onClick={handleOpenDeleteModal}
                                title={translations.risk.margin.headers.customCalculatorDelete}
                                type={LinkButtonType.White}
                                disabled={loading}
                                track={{ eventCategory: 'MarginCalculator', eventAction: 'DeleteClicked' }}
                            />
                        )}
                        <LinkButton
                            onClick={handleSave}
                            title={translations.actions.save}
                            type={LinkButtonType.Blue}
                            disabled={loading}
                            track={{ eventCategory: 'MarginCalculator', eventAction: 'SaveClicked' }}
                        />
                    </div>
                </div>
            </div>
            {hasNoEngineTypes && <p>{translations.risk.margin.messages.customCalculatorNoSaved}</p>}
            {loading && <TableSkeleton />}
            {!loading && displaySelection === 'inputs' && (
                <div ref={inputsTableContainerRef} data-testid="MarginComparisonTable" className={styles.risk_grid_wrapper}>
                    <MarginInputsTable
                        runCalculationEngineResponse={runCalculationEngineResponse!}
                        getFarmerMarginCalculatorCompositeRowsModel={getFarmerMarginCalculatorCompositeRowsModel as MarginCompositeModel[]}
                        quarterOptions={availableQuarters.filter((x) => {
                            const currentQuarter = x.value;
                            return getQuarterRange(currentQuarter);
                        })}
                        hideGroup={hideGroup}
                        hideRows={excludedFuturesBlendPercentRows}
                        setHideGroup={setHideGroup}
                        runMonthlyValueOverrides={runMonthlyValueOverrides}
                        savedFarmerMarginCalculator={activeSavedCalculator}
                        setRunMonthlyValueOverrides={setRunMonthlyValueOverrides}
                        handleRemoveLotLineRow={handleRemoveLotLineRow}
                        track={{ eventCategory: 'MarginCalculator', eventAction: `Calculator_${selectedCalculator?.label}_InputsTable` }}
                    />
                </div>
            )}
            {!loading && displaySelection === 'charts' && runCalculationEngineLoading && <ChartSkeleton />}
            {!loading && displaySelection === 'charts' && !runCalculationEngineLoading && (
                <>
                    <MarginChartsCarousel
                        getFarmerMarginCalculatorCompositeRowsModels={getFarmerMarginCalculatorCompositeRowsModel as MarginCompositeModel[]}
                        runCalculationEngineResponse={runCalculationEngineResponse!}
                        runMonthlyValueOverrides={runMonthlyValueOverrides}
                        isLoading={runCalculationEngineLoading}
                        displayDecimalPlaces={defalutDisplayDecimalPlaces}
                    />
                    {getFarmerMarginCalculatorByGuidResponse?.farmerMarginCalculator?.commodity && (
                        <MarginPositionsHedgeMapCharts
                            runCalculationEngineResponse={runCalculationEngineResponse!}
                            isLoading={runCalculationEngineLoading}
                            displayDecimalPlaces={defalutDisplayDecimalPlaces}
                            getFarmerMarginCalculatorCompositeRowsModels={getFarmerMarginCalculatorCompositeRowsModel as MarginCompositeModel[]}
                            selectedCalculatorCommodity={getFarmerMarginCalculatorByGuidResponse.farmerMarginCalculator.commodity}
                            endingQuarter={marginInputs?.endingQuarter}
                            startingQuarter={marginInputs?.startingQuarter}
                        />
                    )}
                    <MarginHeatMapTable
                        marginInputs={marginInputs!}
                        upsertCalculationEngine={upsertCalculationEngine}
                        runMonthlyValueOverrides={runMonthlyValueOverrides}
                    />
                </>
            )}
            {!loading && displaySelection === 'positions' && (
                <div data-testid="MarginPositionsTable" className={styles.margin_positions_table}>
                    <MarginPositionsTable
                        selectedCalculatorCommodity={getFarmerMarginCalculatorByGuidResponse?.farmerMarginCalculator?.commodity}
                        isCalculatorLoading={getFarmerMarginCalculatorByGuidLoading}
                    />
                </div>
            )}
            <CustomCalculatorModals
                isAddCalculatorNameModalOpen={isAddCalculatorNameModalOpen}
                pendingCalculatorName={pendingCalculatorName}
                onPendingCalculatorNameChange={setPendingCalculatorName}
                onConfirmSaveAs={handleConfirmSaveAs}
                onCancelSaveAs={() => setIsAddCalculatorNameModalOpen(false)}
                isRenameCalculatorModalOpen={isRenameCalculatorModalOpen}
                pendingRenameCalculatorName={pendingRenameCalculatorName}
                onPendingRenameCalculatorNameChange={setPendingRenameCalculatorName}
                onConfirmRename={handleConfirmRename}
                onCancelRename={() => setIsRenameCalculatorModalOpen(false)}
                isDeleteCalculatorModalOpen={isDeleteCalculatorModalOpen}
                deleteCalculatorName={activeSavedCalculator?.name ?? undefined}
                onConfirmDelete={handleConfirmDelete}
                onCancelDelete={() => setIsDeleteCalculatorModalOpen(false)}
                isCreateCalculatorModalOpen={isCreateCalculatorModalOpen}
                pendingCreateCalculatorName={pendingCreateCalculatorName}
                onPendingCreateCalculatorNameChange={setPendingCreateCalculatorName}
                onConfirmCreate={handleConfirmCreate}
                onCancelCreate={() => setIsCreateCalculatorModalOpen(false)}
            />
        </>
    );
};

export default Margin;
